// @ts-check

import { caseKeysFromHhx } from './case-hhx-utils'
import { nullIfEmpty } from './object'
import {
  caseFieldStringMeansNone,
  noneStringTestsByField,
} from './string-means-none'

/** @type {HhxField[]} */
export const keysWeShouldTakeNewer = [
  'height',
  'height_raw',
  'weight',
  'weight_raw',
  'asaClassification',

  // stuff from EMR
  'healthHistorySummary',

  // all the airway stuff, newer is better if we have both
  'mallampati',
  'brodsky',
  'ulbt',
  'tmd',
  'iid',
]

/** @type {HhxField[]} */
export const keysToNeverInclude = [
  /**
   * The medical consult notes only appear based on answer to 'medicalConsult'
   * field so we handle the notes as part of the related field.
   */
  'medicalConsultNotes',
]

const combineNoneField = (fieldName, newerValue, olderValue) => {
  const oldIsNone = caseFieldStringMeansNone(fieldName, olderValue)
  const newIsNone = caseFieldStringMeansNone(fieldName, newerValue)
  // if both are none, just return the newer one
  if (oldIsNone && newIsNone) {
    return newerValue
  }
  // if one is none, return the other
  if (oldIsNone) {
    return newerValue
  }
  if (newIsNone) {
    return olderValue
  }

  // If they are identical, just return one
  if (newerValue === olderValue) {
    return newerValue
  }

  // if neither is none, combined them into a single string
  return `${olderValue}, ${newerValue}`
}

/**
 * @param {CaseData['systemsReview']} systemsReview
 * @returns {CaseData['systemsReview']}
 */
const cleanSystemsReviewObject = systemsReview => {
  const copy = JSON.parse(JSON.stringify(systemsReview))
  for (const key in copy) {
    const entry = copy[key]
    // make sure it doesn't have any entries if it says it doesn't
    if (entry.hasEntries === false) {
      delete entry.entries
    }
  }
  return copy
}

/**
 * @param {HhxData} newer
 * @param {HhxData} older
 * @returns {Partial<HhxData>}
 */
export const mergeHhx = (newer, older) => {
  /** @type {Partial<HhxData>} */
  const result = {}

  let handledMeds = false
  for (const key of caseKeysFromHhx) {
    // Some fields, such as the finalization fields, we skip entirely.
    // @ts-ignore
    if (keysToNeverInclude.includes(key)) {
      continue
    }

    /**
     * For medications we combine the arrays and de-duplicate them if they both
     * have them. Otherwise we ignore the "no" answer and use the one that has them.
     */
    if ((key === 'medications' || key === 'medicationsText') && !handledMeds) {
      handledMeds = true
      if (newer.medications && older.medications) {
        const combinedMeds = [...newer.medications]
        for (const med of older.medications) {
          const found = newer.medications.find(m => m.id === med.id)
          if (!found) {
            combinedMeds.push(med)
          }
        }
        result.medications = combinedMeds
      } else {
        const medications = newer.medications || older.medications
        if (medications) {
          result.medications = medications
        }
      }

      /**
       * If we don't have a medications array on the result, we should use the
       * medicationsText field if either has it since it will just say "none"
       */
      if (
        !result.medications &&
        (newer.medicationsText || older.medicationsText)
      ) {
        result.medicationsText = newer.medicationsText || older.medicationsText
      }
      continue
    }

    const inOlder = older.hasOwnProperty(key)
    const inNewer = newer.hasOwnProperty(key)

    // if not in either skip it
    if (!inOlder && !inNewer) {
      continue
    }

    if (key === 'systemsReviewSkipped') {
      /**
       * If this one exists, it means it's true but since it hides a bunch of
       * stuff we only want to set it to true if both are true.
       */
      if (inNewer && inOlder) {
        // if either is true, we take the newer one
        result.systemsReviewSkipped = true
        continue
      }
    }

    // if a key is only in one keep the one we have
    if (inOlder && !inNewer) {
      // @ts-ignore
      result[key] = older[key]
      continue
    }
    if (inNewer && !inOlder) {
      // @ts-ignore
      result[key] = newer[key]
      continue
    }

    // NB: We know it is in both at this point!

    /**
     * For patient measurements when merging we keep all from both as long as
     * their keys are unique. If they have the same key they're assumed to be
     * the same measurement since they're assigned a unique id and may have been
     * copied from one to the other
     */
    if (key === 'pm') {
      /** @type {{ [key: string]: ManualMeasurement }} */
      const merged = {}
      for (const id in newer.pm) {
        merged[id] = newer.pm[id]
      }
      for (const id in older.pm) {
        if (!merged[id]) {
          merged[id] = older.pm[id]
        }
      }
      result.pm = merged
    }

    // Some keys we should always take the newer value
    if (keysWeShouldTakeNewer.includes(key)) {
      // @ts-ignore
      result[key] = newer[key]
      continue
    }

    /**
     * Some keys with various "none" type string values we have a special way of
     * combining them to avoid duplicates.
     */
    if (noneStringTestsByField[key]) {
      // @ts-ignore
      result[key] = combineNoneField(key, newer[key], older[key])
      continue
    }

    if (key === 'medicalConsult') {
      // if either is yes, we take the newer one
      if (newer.medicalConsult || older.medicalConsult) {
        result.medicalConsult = true
        // we do this to ignore the notes field if the question
        // that makes it appear is false
        const medConsultNotesFromNewer =
          newer.medicalConsult && newer.medicalConsultNotes
        const medConsultNotesFromOlder =
          older.medicalConsult && older.medicalConsultNotes

        // if both have values and are identical, we just take one
        if (
          medConsultNotesFromNewer === medConsultNotesFromOlder &&
          medConsultNotesFromNewer
        ) {
          result.medicalConsultNotes = medConsultNotesFromNewer
        } else {
          // in all other cases we combine any notes we may have
          const combinedNotes = [
            medConsultNotesFromOlder,
            medConsultNotesFromNewer,
          ]
            .filter(x => x)
            .join('\n\n')
          if (combinedNotes) {
            result.medicalConsultNotes = combinedNotes
          }
        }
      } else {
        /**
         * At this point we know both are false because we know both have the
         * field, but we checked above if either is true.
         *
         * So it must be false.
         */
        result.medicalConsult = false
      }
    }

    if (key === 'systemsReview') {
      const olderSystems = cleanSystemsReviewObject(older.systemsReview || {})
      const newSystems = cleanSystemsReviewObject(newer.systemsReview || {})
      const combinedSystems = { ...olderSystems }
      const keysInNew = Object.keys(newSystems)
      for (const systemKey of keysInNew) {
        // if the older one doesn't have this key, just use
        // the newer one.
        if (!olderSystems[systemKey]) {
          combinedSystems[systemKey] = newSystems[systemKey]
          continue
        }

        // NB: at this point we know both have this key
        const olderHasEntries = olderSystems[systemKey].hasEntries
        const newHasEntries = newSystems[systemKey].hasEntries

        if (!olderHasEntries && !newHasEntries) {
          // if neither has entries, just use the newer one
          combinedSystems[systemKey] = newSystems[systemKey]
          continue
        }

        if (olderHasEntries && !newHasEntries) {
          // if the older one has entries and the new one doesn't, keep the older
          combinedSystems[systemKey] = olderSystems[systemKey]
          continue
        }

        if (!olderHasEntries && newHasEntries) {
          // if the older one doesn't have entries and the new one does, keep the new
          combinedSystems[systemKey] = newSystems[systemKey]
          continue
        }

        // NB: at this point we know both have entries
        combinedSystems[systemKey] = {
          hasEntries: true,
          entries: [
            ...olderSystems[systemKey].entries,
            ...newSystems[systemKey].entries,
          ],
        }
      }

      // only set the combined systems if we have any
      if (nullIfEmpty(combinedSystems)) {
        result.systemsReview = combinedSystems
      }
      continue
    }
  }

  return result
}
